import React, { useEffect, useState } from 'react';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { Calendar } from 'primereact/calendar';
import { InputSwitch } from 'primereact/inputswitch';
import { Button } from 'primereact/button';
import Loader from '../../Shared/Loader/Loader';
import api from '../../../utils/apiClient';
import CommonConfig from '../../../utils/constant';
import Toast from '../../Shared/Toast/Toast';
import APIConstant from '../../../utils/pathConstants';
import { useCatchResponse } from '../../../utils/useCatchResponse';

const emptySlot = () => ({
    Name: '',
    Time: null,
    AllowanceBeforeSeconds: 0,
    AllowanceAfterSeconds: 0,
    MarkAttendance: true,
});

const timeStringToDate = (t) => {
    if (!t) return null;
    const [hh, mm] = t.split(':').map(Number);
    const d = new Date();
    d.setHours(hh || 0, mm || 0, 0, 0);
    return d;
};

const dateToTimeString = (d) => {
    if (!d) return '';
    const hh = String(d.getHours()).padStart(2, '0');
    const mm = String(d.getMinutes()).padStart(2, '0');
    return `${hh}:${mm}`;
};

const AddEditAttendanceTypeConfiguration = (props) => {
    const { catchResponse } = useCatchResponse();
    const [securityUserID, setSecurityUserID] = useState('');
    const [organizationId, setOrganizationId] = useState('');
    const [attendanceName, setAttendanceName] = useState('');
    const [attendanceNameErr, setAttendanceNameErr] = useState('');
    const [slots, setSlots] = useState([]);
    const [genericErr, setGenericErr] = useState('');

    useEffect(() => {
        const loginData = CommonConfig.loginData();
        setSecurityUserID(loginData?._id);
        setOrganizationId(loginData?.OrganizationId || '');

        if (props.ID && props.masterData) {
            const m = props.masterData;
            setAttendanceName(m.AttendanceName || '');
            const loaded = Array.isArray(m.Slots)
                ? m.Slots.map((s) => ({
                    Name: s.Name || '',
                    Time: timeStringToDate(s.Time),
                    AllowanceBeforeSeconds: Number(s.AllowanceBeforeSeconds) || 0,
                    AllowanceAfterSeconds: Number(s.AllowanceAfterSeconds) || 0,
                    MarkAttendance: s.MarkAttendance !== false,
                }))
                : [];
            setSlots(loaded);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const updateSlot = (idx, patch) => {
        setSlots((prev) => prev.map((s, i) => (i === idx ? { ...s, ...patch } : s)));
    };

    const addSlot = () => setSlots((prev) => [...prev, emptySlot()]);
    const removeSlot = (idx) => setSlots((prev) => prev.filter((_, i) => i !== idx));

    const validate = () => {
        setGenericErr('');
        let ok = true;
        if (CommonConfig.isEmpty(attendanceName)) {
            ok = false;
            setAttendanceNameErr('Attendance Name is required');
        } else {
            setAttendanceNameErr('');
        }
        if (!slots.length) {
            ok = false;
            setGenericErr('At least one slot is required');
            return ok;
        }
        const invalid = slots.some((s) => CommonConfig.isEmpty(s.Name) || !s.Time);
        if (invalid) {
            ok = false;
            setGenericErr('Each slot must have a Name and Time');
        }
        return ok;
    };

    const buildPayload = () => ({
        id: props.ID || undefined,
        AttendanceName: attendanceName.trim().replace(/\s+/g, ' '),
        Slots: slots.map((s) => ({
            Name: (s.Name || '').trim(),
            Time: dateToTimeString(s.Time),
            AllowanceBeforeSeconds: Number(s.AllowanceBeforeSeconds) || 0,
            AllowanceAfterSeconds: Number(s.AllowanceAfterSeconds) || 0,
            MarkAttendance: !!s.MarkAttendance,
        })),
        OrganizationId: organizationId,
        CreatedBy: securityUserID,
        UpdatedBy: securityUserID,
    });

    const handleSubmit = async () => {
        if (!validate()) return;
        try {
            Loader.show();
            await api
                .post(APIConstant.path.addAttendanceTypeConfiguration, buildPayload())
                .then((res) => {
                    if (res.success) {
                        Toast.success({ message: res.message });
                        props.onCancel();
                    } else {
                        Toast.error({ message: res.message });
                    }
                    Loader.hide();
                })
                .catch((err) => {
                    Loader.hide();
                    catchResponse(err);
                });
        } catch (err) {
            Loader.hide();
            catchResponse(err);
        }
    };

    return (
        <div>
            <div className="p-grid">
                <div className="p-col-12 p-md-6 p-pt-2">
                    <label>Attendance Name<span style={{ color: 'red' }}>*</span></label>
                    <div className="custom-inputs">
                        <InputText
                            className="p-mt-2 p-pl-2 p-w-100"
                            maxLength={200}
                            placeholder="e.g. School Day"
                            value={attendanceName}
                            onChange={(e) => setAttendanceName(e.target.value)}
                        />
                        <span className="error-msg" style={{ color: 'red' }}>{attendanceNameErr}</span>
                    </div>
                </div>
            </div>

            <div className="p-d-flex p-ai-center p-jc-between p-mt-3">
                <h4 style={{ margin: 0 }}>Slots</h4>
                <Button icon="pi pi-plus" label="Add Slot" className="btn-text-action-secondary" onClick={addSlot} type="button" />
            </div>

            {slots.length === 0 ? (
                <div style={{ padding: 24, textAlign: 'center', color: '#888', border: '1px dashed #ddd', borderRadius: 6, marginTop: 10 }}>
                    No slots yet. Click <strong>Add Slot</strong> to add one.
                </div>
            ) : (
                <div style={{ overflowX: 'auto', marginTop: 10 }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr style={{ background: '#f5f6f8', textAlign: 'left' }}>
                                <th style={{ padding: 10, minWidth: 160 }}>Name</th>
                                <th style={{ padding: 10, minWidth: 130 }}>Time</th>
                                <th style={{ padding: 10, minWidth: 150 }}>Allowance Before (sec)</th>
                                <th style={{ padding: 10, minWidth: 150 }}>Allowance After (sec)</th>
                                <th style={{ padding: 10, minWidth: 90 }}>Attendance</th>
                                <th style={{ padding: 10, width: 60 }}></th>
                            </tr>
                        </thead>
                        <tbody>
                            {slots.map((s, i) => (
                                <tr key={i} style={{ borderBottom: '1px solid #eee' }}>
                                    <td style={{ padding: 8 }}>
                                        <InputText
                                            className="p-w-100"
                                            placeholder="e.g. Check In"
                                            value={s.Name}
                                            onChange={(e) => updateSlot(i, { Name: e.target.value })}
                                        />
                                    </td>
                                    <td style={{ padding: 8 }}>
                                        <Calendar
                                            className="p-w-100"
                                            value={s.Time}
                                            onChange={(e) => updateSlot(i, { Time: e.value })}
                                            timeOnly
                                            hourFormat="24"
                                            placeholder="HH:mm"
                                        />
                                    </td>
                                    <td style={{ padding: 8 }}>
                                        <InputNumber
                                            className="p-w-100"
                                            value={s.AllowanceBeforeSeconds}
                                            onValueChange={(e) => updateSlot(i, { AllowanceBeforeSeconds: e.value ?? 0 })}
                                            min={0}
                                            useGrouping={false}
                                        />
                                    </td>
                                    <td style={{ padding: 8 }}>
                                        <InputNumber
                                            className="p-w-100"
                                            value={s.AllowanceAfterSeconds}
                                            onValueChange={(e) => updateSlot(i, { AllowanceAfterSeconds: e.value ?? 0 })}
                                            min={0}
                                            useGrouping={false}
                                        />
                                    </td>
                                    <td style={{ padding: 8 }}>
                                        <InputSwitch
                                            checked={!!s.MarkAttendance}
                                            onChange={(e) => updateSlot(i, { MarkAttendance: e.value })}
                                        />
                                    </td>
                                    <td style={{ padding: 8, textAlign: 'center' }}>
                                        <Button
                                            icon="pi pi-trash"
                                            className="p-button-sm p-button-text"
                                            onClick={() => removeSlot(i)}
                                            type="button"
                                        />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {genericErr && (
                <div className="p-mt-2">
                    <span className="error-msg" style={{ color: 'red' }}>{genericErr}</span>
                </div>
            )}

            <div className="dialog-footer p-d-flex p-ai-center p-jc-end p-mt-3">
                <Button className="btn-text-action-secondary" onClick={() => props.onCancel()} type="button">Cancel</Button>
                <Button className="btn-text-action-primary submit-focus" label="Save" onClick={handleSubmit} type="button" />
            </div>
        </div>
    );
};

export default AddEditAttendanceTypeConfiguration;
